import { A } from '@mobily/ts-belt';
import { Box, CardContent, CardHeader, Grid } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  checkmark: {
    width: '74px',
    display: 'block',
    margin: '0 auto',
  },
  checkmarkCircle: {
    strokeDasharray: 1000,
    strokeDashoffset: 0,
    animation: `${keyframes`
      0% {
        stroke-dashoffset: 1000;
      }
      100% {
        stroke-dashoffset: 0;
      }
    `} 0.9s ease-in-out`,
  },
  checkmarkCheck: {
    strokeDasharray: 1000,
    strokeDashoffset: 1000,
    animation: `${keyframes`
      0% {
        stroke-dashoffset: 1000;
      }
      100% {
        stroke-dashoffset: 0;
      }
    `} 0.5s 0.35s ease-in-out forwards`,
  },
}));

const redirectTimeout = 2_500;
const totalNumDots = 3;

export interface Properties {
  navigate: () => void;
}

const SuccessfulSplashScreen: FC<Properties> = ({ navigate }) => {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('auth');

  const [dots, setDots] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(
      () => {
        setDots(Math.min(totalNumDots, dots + 1));
      },
      Math.floor(redirectTimeout / totalNumDots),
    );

    return () => {
      clearTimeout(intervalId);
    };
  }, [dots]);

  useEffect(() => {
    const redirectTimer = setTimeout(navigate, redirectTimeout);

    return () => {
      clearTimeout(redirectTimer);
    };
  }, []);

  const preservedPosition = (
    <>
      {A.repeat(totalNumDots - dots, '.').map((dot, i) => (
        <Box component="span" key={i} sx={{ opacity: 0 }}>
          {dot}
        </Box>
      ))}
    </>
  );

  return (
    <>
      <CardHeader
        titleTypographyProps={{ align: 'center' }}
        subheaderTypographyProps={{ align: 'center' }}
        title={t('auth:successfullySignedIn', 'Successfully signed in!')}
        subheader={
          <Trans i18nKey="auth:navigating">
            Navigating to the application{'.'.repeat(dots)}
            {preservedPosition}
          </Trans>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <svg
              className={classes.checkmark}
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 130.2 130.2"
            >
              <circle
                className={classes.checkmarkCircle}
                fill="none"
                stroke={theme.palette.success.main}
                strokeWidth="9"
                strokeMiterlimit="10"
                cx="65.1"
                cy="65.1"
                r="60.1"
              />
              <polyline
                className={classes.checkmarkCheck}
                fill="none"
                stroke={theme.palette.success.main}
                strokeWidth="9"
                strokeLinecap="round"
                strokeMiterlimit="10"
                points="100.2,40.2 51.5,88.8 29.8,67.5 "
              />
            </svg>
          </Grid>
        </Grid>
      </CardContent>
    </>
  );
};

export default SuccessfulSplashScreen;
